import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { SocialVideo } from '../../admin/components/SocialTab';

const getEmbedUrl = (plataforma: 'tiktok' | 'instagram', url: string): string => {
  if (plataforma === 'tiktok') {
    const match = url.match(/\/video\/(\d+)/);
    if (match) return `https://www.tiktok.com/embed/v2/${match[1]}`;
    return '';
  }
  if (plataforma === 'instagram') {
    const match = url.match(/\/(reel|p)\/([A-Za-z0-9_-]+)/);
    if (match) return `https://www.instagram.com/p/${match[2]}/embed`;
    return '';
  }
  return '';
};

const platformBadge = {
  tiktok:    { icon: 'ri-tiktok-line',    label: 'TikTok',    cls: 'bg-zinc-900 text-white border-zinc-700' },
  instagram: { icon: 'ri-instagram-line', label: 'Instagram', cls: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
};

interface VideoCardProps {
  video: SocialVideo;
}

const VideoCard = ({ video }: VideoCardProps) => {
  const [playing, setPlaying] = useState(false);
  const embedUrl = getEmbedUrl(video.plataforma, video.url);
  const badge = platformBadge[video.plataforma];
  const isVertical = video.plataforma === 'tiktok';

  return (
    <div className="bg-zinc-900 border border-zinc-800 overflow-hidden group transition-all duration-300 hover:border-zinc-600 flex flex-col">
      {/* Embed o Thumbnail */}
      <div
        className="relative bg-zinc-950 flex items-center justify-center overflow-hidden"
        style={{ height: isVertical ? 420 : 340 }}
      >
        {playing && embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title={video.titulo}
          />
        ) : (
          <>
            {/* Play overlay */}
            <div className="absolute inset-0 bg-zinc-950 flex flex-col items-center justify-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${badge.cls}`}>
                <i className={`${badge.icon} text-lg`} />
              </div>
              <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold">{badge.label}</p>
            </div>
            {/* Hover play button */}
            <button
              onClick={() => setPlaying(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all duration-300 cursor-pointer"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                <i className="ri-play-fill text-black text-2xl ml-1" />
              </div>
            </button>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 px-4 py-3 border-t border-zinc-800">
        <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded border ${badge.cls}`}>
          <i className={`${badge.icon} text-xs`} />
        </div>
        <p className="text-zinc-300 text-sm font-medium flex-1 min-w-0 truncate">{video.titulo}</p>
        <a
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-7 h-7 border border-zinc-700 text-zinc-500 hover:text-yellow-400 hover:border-yellow-400/40 transition-all cursor-pointer flex-shrink-0"
          title={`Ver en ${badge.label}`}
        >
          <i className="ri-external-link-line text-xs" />
        </a>
      </div>
    </div>
  );
};

const SocialSection = () => {
  const [videos, setVideos] = useState<SocialVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'tiktok' | 'instagram'>('all');

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'social_videos')
        .maybeSingle();
      if (data?.value) setVideos(data.value as SocialVideo[]);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = filter === 'all' ? videos : videos.filter((v) => v.plataforma === filter);
  const hasTikTok = videos.some((v) => v.plataforma === 'tiktok');
  const hasInstagram = videos.some((v) => v.plataforma === 'instagram');

  return (
    <section className="py-20 bg-zinc-950" id="social">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-0.5 bg-yellow-400" />
              <span className="text-yellow-400 text-xs font-black uppercase tracking-widest">Síguenos</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight">
              Nuestro contenido<br />
              <span className="text-yellow-400">en redes sociales</span>
            </h2>
            <p className="text-zinc-400 text-sm mt-3 max-w-md">
              Mira cómo trabajamos, trucos de mecánica y el día a día del taller. ¡Síguenos para no perderte nada!
            </p>
          </div>

          {/* Filtros */}
          {(hasTikTok && hasInstagram) && (
            <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 p-1 self-start md:self-auto">
              {([
                { id: 'all',       label: 'Todo',      icon: 'ri-apps-line' },
                { id: 'tiktok',    label: 'TikTok',    icon: 'ri-tiktok-line' },
                { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
              ] as const).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide cursor-pointer whitespace-nowrap transition-all ${
                    filter === f.id
                      ? 'bg-yellow-400 text-black'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <i className={f.icon} />
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-yellow-400 text-3xl" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border border-dashed border-zinc-800 gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded-full">
                <i className="ri-tiktok-line text-zinc-500 text-xl" />
              </div>
              <div className="w-12 h-12 flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded-full">
                <i className="ri-instagram-line text-zinc-500 text-xl" />
              </div>
            </div>
            <p className="text-zinc-500 text-sm font-medium">Próximamente contenido en redes sociales</p>
            <p className="text-zinc-700 text-xs">¡Síguenos para no perderte nada del taller!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        )}

        {/* CTA links */}
        {!loading && videos.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            {hasTikTok && (
              <a
                href="https://www.tiktok.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-tiktok-line" />
                Seguir en TikTok
              </a>
            )}
            {hasInstagram && (
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 text-pink-400 text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-instagram-line" />
                Seguir en Instagram
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialSection;
